/**
 * gate.ts — the Mixture-of-Bandits bypass-risk gate.
 *
 * A small L2-regularised logistic-regression classifier that decides, each
 * tick, whether the planner is about to pick a length-8 leap that bypasses
 * nearby gold. When P(bypass | state) > τ the gate FIRES: length-8 actions
 * (indices 24..31) are masked out of the planner's argmax, forcing it to
 * pick a short hop instead.
 *
 * This is the validated v4 design from `sequential_recommender_moe.ipynb`:
 *   - Same Q-function in both branches (no mixture of value functions).
 *   - The gate is a *policy selector*, not a Q-mixer.
 *   - Labels come from observed bypass events (see gate_labels.ts); the
 *     truth grid is read ONLY at label-collection time, never at inference.
 *
 * Why logistic + IRLS:
 *   - The bypass signal is approximately linearly separable in the 6 belief/
 *     history features (notebook §6 measured AUC ≈ 0.76). Linear is enough.
 *   - IRLS (iteratively reweighted least squares) is the Newton method for
 *     logistic regression. Each iteration is a weighted least-squares solve
 *     — we reuse the existing choleskySolve() from linalg.ts, so this module
 *     adds zero new dependencies.
 *   - 8 iterations is comfortably convergent on the typical label set
 *     (~1500 samples); each iteration is one Cholesky solve of a 7×7 system.
 *
 * Style mirrors qnet.ts: single Float64Array of length N_GATE_W = N_GATE_FEAT + 1
 * (features then bias); reset/exportWeights/loadWeights match the LinearQ
 * surface so the Agent and main.ts can treat the two interchangeably.
 */

import { choleskySolve, sigmoid } from './linalg';
import type { Env } from './environment';
import { SENSOR_RADIUS, GOLD } from './belief';

/** Number of gate input features (must match computeGateFeatures below). */
export const N_GATE_FEAT = 6;
/** Weight vector length (features + bias). */
export const N_GATE_W = N_GATE_FEAT + 1;

/** Default L2 regularisation strength (matches sklearn C=1 → λ=1). */
export const DEFAULT_GATE_LAMBDA = 1.0;
/** IRLS iteration count. 8 is comfortably convergent on ~1.5k samples. */
export const DEFAULT_GATE_ITERS = 8;
/** Default gate firing threshold τ. */
export const DEFAULT_GATE_TAU = 0.5;
/** Default minimum label count before fitGate() will fit. Below this the
 *  gate stays OFF (we don't have enough data to trust a fit). */
export const DEFAULT_GATE_MIN_SAMPLES = 200;

/**
 * Build the 6-feature gate input from the agent's legitimate observation.
 * NO TRUTH QUERIES — every value comes from `env.belief` (built one noisy
 * tick at a time) or the agent's own recent-event ring.
 *
 *   [0] local_belief_mean   mean bGold over sensor radius
 *   [1] local_belief_max    peak bGold in sensor radius
 *   [2] local_minus_global  local mean − global mean (regime signal)
 *   [3] gold_last_3_steps   count of 'gold' events in last 3 step results
 *   [4] gold_last_5_steps   count in last 5
 *   [5] step_in_episode     env.steps / env.maxSteps
 *
 * `recentGoldRing` is an 8-slot ring buffer of 0/1 gold-event flags the
 * Agent maintains; we read the last 3 and 5 entries to derive features 3-4.
 * The ring is a Uint8Array paired with an integer head index (see Agent).
 */
export function computeGateFeatures(
  env: Env,
  recentGoldRing: Uint8Array,
  ringHead: number,
  out: Float64Array,
): Float64Array {
  const b = env.belief;
  const r = env.r;
  const c = env.c;
  const grid = b.grid;
  // Aggregate bGold over the sensor radius.
  let localSum = 0;
  let localMax = 0;
  let localN = 0;
  for (let dr = -SENSOR_RADIUS; dr <= SENSOR_RADIUS; dr++) {
    for (let dc = -SENSOR_RADIUS; dc <= SENSOR_RADIUS; dc++) {
      if (dr === 0 && dc === 0) continue;
      const nr = r + dr;
      const nc = c + dc;
      if (nr < 0 || nr >= grid || nc < 0 || nc >= grid) continue;
      const v = b.bGold[nr * grid + nc];
      localSum += v;
      if (v > localMax) localMax = v;
      localN++;
    }
  }
  const localMean = localN > 0 ? localSum / localN : 0;
  const globalMean = mean(b.bGold);
  // Recent gold events: count the last 3 and 5 entries of the ring.
  let g3 = 0;
  let g5 = 0;
  for (let k = 1; k <= 5; k++) {
    // Most recent entry is at (ringHead - 1 + 8) % 8, then back from there.
    const idx = ((ringHead - k) % recentGoldRing.length + recentGoldRing.length) % recentGoldRing.length;
    if (recentGoldRing[idx] > 0) {
      if (k <= 3) g3++;
      g5++;
    }
  }
  out[0] = localMean;
  out[1] = localMax;
  out[2] = localMean - globalMean;
  out[3] = g3;
  out[4] = g5;
  out[5] = env.maxSteps > 0 ? env.steps / env.maxSteps : 0;
  return out;
}

function mean(a: Float64Array): number {
  let s = 0;
  for (let i = 0; i < a.length; i++) s += a[i];
  return a.length > 0 ? s / a.length : 0;
}

export interface GateOptions {
  /** L2 regularisation strength. */
  lambda?: number;
  /** IRLS iteration count. */
  iters?: number;
  /** Firing threshold τ. */
  tau?: number;
  /** Minimum label count before fit() will fit. */
  minSamples?: number;
}

export class Gate {
  /** Weight vector — features then bias. Length N_GATE_FEAT + 1. */
  w: Float64Array;
  /** L2 strength (read at fit-time; mutating is safe). */
  lambda: number;
  /** IRLS iteration count. */
  iters: number;
  /** Firing threshold τ. The Agent reads this at action-selection time. */
  tau: number;
  /** Minimum label count before fit() will fit. */
  minSamples: number;
  /** True once fit() has succeeded at least once. The Agent reads this to
   *  decide whether to actually apply the mask. */
  fitted = false;
  /** Training accuracy from the last successful fit (for the HUD). */
  trainAcc = 0;
  /** Sample count from the last successful fit. */
  trainSize = 0;

  constructor(opts: GateOptions = {}) {
    this.w = new Float64Array(N_GATE_W);
    this.lambda = opts.lambda ?? DEFAULT_GATE_LAMBDA;
    this.iters = opts.iters ?? DEFAULT_GATE_ITERS;
    this.tau = opts.tau ?? DEFAULT_GATE_TAU;
    this.minSamples = opts.minSamples ?? DEFAULT_GATE_MIN_SAMPLES;
  }

  /**
   * Predict P(bypass | x) for one feature row. `x` must be length
   * N_GATE_FEAT. Numerically stable sigmoid of w · x augmented with the
   * bias term (the trailing slot of w).
   */
  predictProba(x: Float64Array): number {
    let z = this.w[N_GATE_FEAT]; // bias
    for (let i = 0; i < N_GATE_FEAT; i++) z += this.w[i] * x[i];
    return sigmoid(z);
  }

  /**
   * Fit the gate to (X, y) by IRLS — the Newton method for logistic
   * regression. Each iteration is a weighted least-squares solve reusing
   * the existing choleskySolve(). With class balancing.
   *
   * `X` is (B × N_GATE_FEAT) flat row-major; `y` is a Uint8Array of 0/1.
   * Returns true on successful fit, false if we bailed (too few samples,
   * or Cholesky failed on a degenerate iteration — perfect separation).
   *
   * Class balancing: sklearn's `class_weight='balanced'` rescales each
   * sample's weight by `B / (n_classes * count(class))`. We implement the
   * same: positive (y=1) samples get `B / (2 * n_pos)`, negatives get
   * `B / (2 * n_neg)`. This matters because the bypass rate is ~25% so
   * unweighted logistic would learn "always predict no-bypass".
   */
  fit(X: Float64Array, y: Uint8Array, B: number): boolean {
    if (B < this.minSamples) return false;
    let nPos = 0;
    for (let k = 0; k < B; k++) if (y[k] > 0) nPos++;
    if (nPos === 0 || nPos === B) return false; // one-class — nothing to learn
    const nNeg = B - nPos;
    const wPos = B / (2 * nPos);
    const wNeg = B / (2 * nNeg);

    const d = N_GATE_W;
    const G = new Float64Array(d * d);
    const rhs = new Float64Array(d);
    const p = new Float64Array(B); // current P(bypass) for each sample

    // Local copy of weights so we can bail mid-iteration on Cholesky failure
    // without corrupting this.w.
    const wCur = new Float64Array(d);

    for (let iter = 0; iter < this.iters; iter++) {
      // 1. Predictions p_k = σ(w · x_k).
      for (let k = 0; k < B; k++) {
        const off = k * N_GATE_FEAT;
        let z = wCur[N_GATE_FEAT];
        for (let i = 0; i < N_GATE_FEAT; i++) z += wCur[i] * X[off + i];
        p[k] = sigmoid(z);
      }
      // 2. Build the weighted normal equations:
      //    G = Aᵀ W A + λI (no ridge on bias),  rhs = Aᵀ W (y - p) + (existing G regularised? No — rebuild each iter).
      //    IRLS: working response is z_k = (y_k - p_k) / (p_k (1 - p_k)), weight is p_k (1 - p_k) * classW.
      //    Equivalent reduced form: rhs = Aᵀ W' (y - p) where W' = classW * p * (1 - p),
      //    and G = Aᵀ W' A. We compute it directly to skip forming z.
      G.fill(0);
      rhs.fill(0);
      for (let k = 0; k < B; k++) {
        const off = k * N_GATE_FEAT;
        const pk = p[k];
        // Clip to avoid div-by-zero in the weighting; matches sklearn's
        // robustness behaviour when prediction approaches 0 or 1.
        const pmc = Math.max(pk * (1 - pk), 1e-8);
        const cw = y[k] > 0 ? wPos : wNeg;
        const wprime = cw * pmc;
        // Outer product into G and weighted-residual into rhs.
        for (let i = 0; i < N_GATE_FEAT; i++) {
          const xi = X[off + i];
          if (xi === 0) continue;
          const rowOff = i * d;
          for (let j = 0; j < N_GATE_FEAT; j++) {
            G[rowOff + j] += wprime * xi * X[off + j];
          }
          G[rowOff + N_GATE_FEAT] += wprime * xi;
          rhs[i] += wprime * xi * (y[k] - pk);
        }
        // Bias row/col.
        G[N_GATE_FEAT * d + N_GATE_FEAT] += wprime;
        const biasRow = N_GATE_FEAT * d;
        for (let j = 0; j < N_GATE_FEAT; j++) G[biasRow + j] += wprime * X[off + j];
        rhs[N_GATE_FEAT] += wprime * (y[k] - pk);
      }
      // 3. Ridge term on feature block only (not bias).
      for (let i = 0; i < N_GATE_FEAT; i++) G[i * d + i] += this.lambda;
      // 4. Solve G · delta = rhs via Cholesky; the IRLS Newton step adds
      //    delta to the current weights.
      let delta: Float64Array;
      try {
        delta = choleskySolve(G, rhs, d);
      } catch (e) {
        // Non-PD pivot — perfect separation or numerical blow-up. Keep the
        // weights from the last successful iteration (or zeros if iter 0).
        break;
      }
      for (let i = 0; i < d; i++) wCur[i] += delta[i];
    }
    // Adopt the iterated weights.
    this.w.set(wCur);
    this.fitted = true;
    this.trainSize = B;
    // Compute training accuracy for the HUD.
    let correct = 0;
    for (let k = 0; k < B; k++) {
      const off = k * N_GATE_FEAT;
      let z = this.w[N_GATE_FEAT];
      for (let i = 0; i < N_GATE_FEAT; i++) z += this.w[i] * X[off + i];
      const pk = sigmoid(z);
      const pred = pk > 0.5 ? 1 : 0;
      if (pred === y[k]) correct++;
    }
    this.trainAcc = correct / B;
    return true;
  }

  /** Reset weights to zero (untrained). */
  reset(): void {
    this.w.fill(0);
    this.fitted = false;
    this.trainAcc = 0;
    this.trainSize = 0;
  }

  /** Export weights as a plain number[] (for save/JSON). Length N_GATE_FEAT+1. */
  exportWeights(): number[] {
    return Array.from(this.w);
  }

  /** Load weights from a number[] of length N_GATE_FEAT+1. Throws on mismatch. */
  loadWeights(w: ArrayLike<number>): void {
    if (w.length !== N_GATE_W) {
      throw new Error(
        `loadWeights: expected length ${N_GATE_W}, got ${w.length} — layout drift`,
      );
    }
    for (let i = 0; i < N_GATE_W; i++) this.w[i] = w[i];
    this.fitted = true;
  }
}

/**
 * Convenience: compute gate features and predict in one call. Returns
 * P(bypass | state) ∈ [0, 1]. Useful in tests and the HUD's per-tick read.
 */
export function gateBypassProb(
  gate: Gate,
  env: Env,
  recentGoldRing: Uint8Array,
  ringHead: number,
  featBuf: Float64Array,
): number {
  computeGateFeatures(env, recentGoldRing, ringHead, featBuf);
  return gate.predictProba(featBuf);
}

// Used by gate_labels.ts to detect gold cells in the truth grid.
export { GOLD };
